/**
 * GolfAI - AI opponent for the Golf card game with three difficulty levels.
 *
 * Easy: Random valid moves with slight preference for lower-value cards.
 *       40% suboptimal moves, 500ms thinking time.
 * Medium: Basic strategy with card tracking, column-pair seeking.
 *         10% suboptimal moves, 1000ms thinking time.
 * Hard: Card counting, probability estimation, optimal column-pair strategy.
 *       0% suboptimal moves, 2000ms thinking time.
 */

import {
  Card,
  CardGrid,
  GridPosition,
  GolfDifficulty,
  GolfGameState,
  AIAction,
  getCardValue,
} from './types';

export class GolfAI {
  difficulty: GolfDifficulty;

  /** Tracks all visible cards for hard difficulty card counting */
  private visibleCards: Card[] = [];

  constructor(difficulty: GolfDifficulty = 'easy') {
    this.difficulty = difficulty;
  }

  // ==========================================================================
  // Public Interface
  // ==========================================================================

  /**
   * Choose 2 positions to reveal at the start of a round.
   */
  chooseInitialReveals(grid: CardGrid): GridPosition[] {
    switch (this.difficulty) {
      case 'easy':
        return this.easyInitialReveals(grid);
      case 'medium':
        return this.mediumInitialReveals(grid);
      case 'hard':
        return this.hardInitialReveals(grid);
    }
  }

  /**
   * Choose whether to draw from the draw pile or discard pile.
   */
  chooseDrawSource(gameState: GolfGameState): 'draw-pile' | 'discard-pile' {
    switch (this.difficulty) {
      case 'easy':
        return this.easyDrawSource(gameState);
      case 'medium':
        return this.mediumDrawSource(gameState);
      case 'hard':
        return this.hardDrawSource(gameState);
    }
  }

  /**
   * Choose what to do with the drawn card: replace a grid card or discard.
   */
  chooseAction(drawnCard: Card, gameState: GolfGameState): AIAction {
    switch (this.difficulty) {
      case 'easy':
        return this.easyAction(drawnCard, gameState);
      case 'medium':
        return this.mediumAction(drawnCard, gameState);
      case 'hard':
        return this.hardAction(drawnCard, gameState);
    }
  }

  /**
   * Choose a face-down card to flip after discarding.
   */
  chooseFlipPosition(grid: CardGrid): GridPosition {
    switch (this.difficulty) {
      case 'easy':
        return this.easyFlipPosition(grid);
      case 'medium':
        return this.mediumFlipPosition(grid);
      case 'hard':
        return this.hardFlipPosition(grid);
    }
  }

  /**
   * Set the AI difficulty level.
   */
  setDifficulty(difficulty: GolfDifficulty): void {
    this.difficulty = difficulty;
    this.visibleCards = [];
  }

  /**
   * Get the thinking time delay in milliseconds for the current difficulty.
   */
  getThinkingTime(): number {
    switch (this.difficulty) {
      case 'easy':
        return 500;
      case 'medium':
        return 1000;
      case 'hard':
        return 2000;
    }
  }


  // ==========================================================================
  // Easy Difficulty Strategies
  // ==========================================================================

  /**
   * Easy: Pick 2 random positions to reveal.
   */
  private easyInitialReveals(grid: CardGrid): GridPosition[] {
    const positions: GridPosition[] = [];
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        if (!grid[row][col].faceUp) {
          positions.push({ row, col });
        }
      }
    }
    return this.shuffleArray(positions).slice(0, 2);
  }

  /**
   * Easy: Random draw source with slight preference for low discard values.
   * 40% of the time makes a suboptimal choice (ignores discard value).
   */
  private easyDrawSource(gameState: GolfGameState): 'draw-pile' | 'discard-pile' {
    const discardTop = gameState.discardPile.length > 0
      ? gameState.discardPile[gameState.discardPile.length - 1]
      : null;

    // 40% chance of random (suboptimal) choice
    if (Math.random() < 0.4) {
      return Math.random() < 0.5 ? 'draw-pile' : 'discard-pile';
    }

    // Slight preference: take from discard if value <= 3
    if (discardTop && getCardValue(discardTop) <= 3) {
      return 'discard-pile';
    }

    return 'draw-pile';
  }

  /**
   * Easy: Random valid action with slight preference for replacing higher-value cards.
   * 40% suboptimal moves.
   */
  private easyAction(drawnCard: Card, gameState: GolfGameState): AIAction {
    const aiPlayerIndex = gameState.currentPlayerIndex;
    const grid = gameState.players[aiPlayerIndex].grid;
    const drawnValue = getCardValue(drawnCard);

    // 40% chance of purely random action
    if (Math.random() < 0.4) {
      if (Math.random() < 0.4) {
        return { type: 'discard' };
      }
      // Pick a random position
      const allPositions: GridPosition[] = [];
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 3; col++) {
          allPositions.push({ row, col });
        }
      }
      const pos = allPositions[Math.floor(Math.random() * allPositions.length)];
      return { type: 'replace', position: pos };
    }

    // Slight preference: replace a face-up card with higher value, or a face-down card
    const candidates: { position: GridPosition; value: number }[] = [];
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const cell = grid[row][col];
        if (cell.faceUp) {
          const cellValue = getCardValue(cell.card);
          if (cellValue > drawnValue) {
            candidates.push({ position: { row, col }, value: cellValue });
          }
        } else {
          // Face-down cards have unknown value; slight preference to replace if drawn is low
          if (drawnValue <= 4) {
            candidates.push({ position: { row, col }, value: 5 }); // Assume average
          }
        }
      }
    }

    if (candidates.length > 0) {
      // Pick a random candidate (not necessarily the best one)
      const chosen = candidates[Math.floor(Math.random() * candidates.length)];
      return { type: 'replace', position: chosen.position };
    }

    return { type: 'discard' };
  }

  /**
   * Easy: Pick a random face-down card to flip.
   */
  private easyFlipPosition(grid: CardGrid): GridPosition {
    const faceDown: GridPosition[] = [];
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        if (!grid[row][col].faceUp) {
          faceDown.push({ row, col });
        }
      }
    }
    if (faceDown.length === 0) {
      // Fallback: should not happen in normal play
      return { row: 0, col: 0 };
    }
    return faceDown[Math.floor(Math.random() * faceDown.length)];
  }


  // ==========================================================================
  // Medium Difficulty Strategies
  // ==========================================================================

  /**
   * Medium: Reveal bottom row cards (more likely to want to replace top row).
   * Slight strategic thinking about which cards to reveal.
   */
  private mediumInitialReveals(grid: CardGrid): GridPosition[] {
    const positions: GridPosition[] = [];
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        if (!grid[row][col].faceUp) {
          positions.push({ row, col });
        }
      }
    }
    // Prefer revealing one from each row for information
    const topRow = positions.filter(p => p.row === 0);
    const bottomRow = positions.filter(p => p.row === 1);

    if (topRow.length > 0 && bottomRow.length > 0) {
      return [
        topRow[Math.floor(Math.random() * topRow.length)],
        bottomRow[Math.floor(Math.random() * bottomRow.length)],
      ];
    }
    return this.shuffleArray(positions).slice(0, 2);
  }

  /**
   * Medium: Consider discard pile top card value before deciding.
   * Take from discard if it's low value or creates a column pair.
   * 10% suboptimal moves.
   */
  private mediumDrawSource(gameState: GolfGameState): 'draw-pile' | 'discard-pile' {
    // 10% chance of suboptimal random choice
    if (Math.random() < 0.1) {
      return Math.random() < 0.5 ? 'draw-pile' : 'discard-pile';
    }

    const discardTop = gameState.discardPile.length > 0
      ? gameState.discardPile[gameState.discardPile.length - 1]
      : null;

    if (!discardTop) {
      return 'draw-pile';
    }

    const discardValue = getCardValue(discardTop);
    const aiPlayerIndex = gameState.currentPlayerIndex;
    const grid = gameState.players[aiPlayerIndex].grid;

    // Check if discard card can create a column pair
    if (this.canCreateColumnPair(discardTop, grid)) {
      return 'discard-pile';
    }

    // Take from discard if value is low (good replacement candidate)
    if (discardValue <= 3) {
      return 'discard-pile';
    }

    // Check if we have high-value face-up cards that this could replace
    const highestFaceUp = this.getHighestFaceUpValue(grid);
    if (discardValue < highestFaceUp - 2) {
      return 'discard-pile';
    }

    return 'draw-pile';
  }

  /**
   * Medium: Replace highest-value known card with lower drawn card.
   * Actively seek column-pair matches. 10% suboptimal moves.
   */
  private mediumAction(drawnCard: Card, gameState: GolfGameState): AIAction {
    const aiPlayerIndex = gameState.currentPlayerIndex;
    const grid = gameState.players[aiPlayerIndex].grid;
    const drawnValue = getCardValue(drawnCard);

    // 10% chance of suboptimal action
    if (Math.random() < 0.1) {
      return this.easyAction(drawnCard, gameState);
    }

    // Priority 1: Create a column pair (highest value strategy)
    const pairPosition = this.findColumnPairPosition(drawnCard, grid);
    if (pairPosition) {
      return { type: 'replace', position: pairPosition };
    }

    // Priority 2: Replace the highest-value face-up card if drawn is lower
    const highestFaceUp = this.findHighestFaceUpPosition(grid);
    if (highestFaceUp && drawnValue < highestFaceUp.value - 1) {
      return { type: 'replace', position: highestFaceUp.position };
    }

    // Priority 3: Replace a face-down card if drawn value is very low
    if (drawnValue <= 2) {
      const faceDown = this.getFaceDownPositions(grid);
      if (faceDown.length > 0) {
        // Prefer positions that could create column pairs
        const bestFaceDown = this.findBestFaceDownForReplacement(drawnCard, grid, faceDown);
        return { type: 'replace', position: bestFaceDown };
      }
    }

    // Priority 4: Replace face-down card if drawn is King (value 0)
    if (drawnCard.rank === 'K') {
      const faceDown = this.getFaceDownPositions(grid);
      if (faceDown.length > 0) {
        const bestFaceDown = this.findBestFaceDownForReplacement(drawnCard, grid, faceDown);
        return { type: 'replace', position: bestFaceDown };
      }
      // Even replace a face-up card if it's high value
      if (highestFaceUp && highestFaceUp.value > 3) {
        return { type: 'replace', position: highestFaceUp.position };
      }
    }

    return { type: 'discard' };
  }

  /**
   * Medium: Flip a face-down card that could potentially create a column pair.
   */
  private mediumFlipPosition(grid: CardGrid): GridPosition {
    const faceDown = this.getFaceDownPositions(grid);
    if (faceDown.length === 0) {
      return { row: 0, col: 0 };
    }

    // Prefer flipping a card in a column where the other card is face-up
    // (reveals information about potential column pairs)
    const preferred: GridPosition[] = [];
    for (const pos of faceDown) {
      const otherRow = pos.row === 0 ? 1 : 0;
      if (grid[otherRow][pos.col].faceUp) {
        preferred.push(pos);
      }
    }

    if (preferred.length > 0) {
      return preferred[Math.floor(Math.random() * preferred.length)];
    }

    return faceDown[Math.floor(Math.random() * faceDown.length)];
  }


  // ==========================================================================
  // Hard Difficulty Strategies
  // ==========================================================================

  /**
   * Hard: Reveal cards in different columns to maximize information for
   * column-pair strategy planning.
   */
  private hardInitialReveals(grid: CardGrid): GridPosition[] {
    const positions: GridPosition[] = [];
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        if (!grid[row][col].faceUp) {
          positions.push({ row, col });
        }
      }
    }

    // Reveal one card from each of two different columns for maximum info
    // Prefer revealing in columns where we can plan pairs
    const col0 = positions.filter(p => p.col === 0);
    const col1 = positions.filter(p => p.col === 1);
    const col2 = positions.filter(p => p.col === 2);

    const columns = [col0, col1, col2].filter(c => c.length > 0);
    if (columns.length >= 2) {
      // Pick from two different columns
      const first = columns[0][Math.floor(Math.random() * columns[0].length)];
      const second = columns[1][Math.floor(Math.random() * columns[1].length)];
      return [first, second];
    }

    return this.shuffleArray(positions).slice(0, 2);
  }

  /**
   * Hard: Evaluate expected value when choosing draw source.
   * Uses card counting to estimate draw pile composition.
   */
  private hardDrawSource(gameState: GolfGameState): 'draw-pile' | 'discard-pile' {
    const discardTop = gameState.discardPile.length > 0
      ? gameState.discardPile[gameState.discardPile.length - 1]
      : null;

    if (!discardTop) {
      return 'draw-pile';
    }

    const aiPlayerIndex = gameState.currentPlayerIndex;
    const grid = gameState.players[aiPlayerIndex].grid;
    const discardValue = getCardValue(discardTop);

    // Check if discard card creates a column pair — always take it
    if (this.canCreateColumnPair(discardTop, grid)) {
      return 'discard-pile';
    }

    // Calculate the benefit of taking the discard card
    const discardBenefit = this.calculateReplacementBenefit(discardTop, grid);

    // Estimate expected value from draw pile using card counting
    const expectedDrawValue = this.estimateDrawPileExpectedValue(gameState);

    // Calculate expected benefit from draw pile
    // Average benefit: how much we'd improve by drawing an average card
    const expectedDrawBenefit = this.estimateExpectedDrawBenefit(grid, expectedDrawValue);

    // Take discard if its benefit exceeds expected draw benefit
    if (discardBenefit > expectedDrawBenefit && discardBenefit > 0) {
      return 'discard-pile';
    }

    // Take discard if it's a King (always 0 points)
    if (discardTop.rank === 'K') {
      const faceDown = this.getFaceDownPositions(grid);
      const highFaceUp = this.findHighestFaceUpPosition(grid);
      if (faceDown.length > 0 || (highFaceUp && highFaceUp.value > 0)) {
        return 'discard-pile';
      }
    }

    return 'draw-pile';
  }

  /**
   * Hard: Optimal action considering column pairs, card counting, and position values.
   * 0% suboptimal moves.
   */
  private hardAction(drawnCard: Card, gameState: GolfGameState): AIAction {
    const aiPlayerIndex = gameState.currentPlayerIndex;
    const grid = gameState.players[aiPlayerIndex].grid;
    const drawnValue = getCardValue(drawnCard);

    // Priority 1: Create a column pair (cancels both cards to 0)
    const pairPosition = this.findColumnPairPosition(drawnCard, grid);
    if (pairPosition) {
      // Verify the pair is actually beneficial
      const otherRow = pairPosition.row === 0 ? 1 : 0;
      const otherCell = grid[otherRow][pairPosition.col];
      if (otherCell.faceUp) {
        // Creating a pair saves both card values
        const savedPoints = getCardValue(otherCell.card) + drawnValue;
        // Only skip if the card we're replacing is already in a pair
        const currentCell = grid[pairPosition.row][pairPosition.col];
        if (currentCell.faceUp) {
          const currentValue = getCardValue(currentCell.card);
          // Net benefit: we save the pair value but lose the current card's removal
          if (savedPoints > currentValue) {
            return { type: 'replace', position: pairPosition };
          }
        } else {
          // Replacing face-down with pair match is almost always good
          // Expected face-down value is ~5.5, pair saves drawnValue + otherValue
          return { type: 'replace', position: pairPosition };
        }
      }
    }

    // Priority 2: Find the optimal replacement position
    const bestReplacement = this.findOptimalReplacement(drawnCard, grid, gameState);
    if (bestReplacement) {
      return { type: 'replace', position: bestReplacement };
    }

    return { type: 'discard' };
  }

  /**
   * Hard: Flip the face-down card most likely to create a column pair
   * or in the column with highest potential benefit.
   */
  private hardFlipPosition(grid: CardGrid): GridPosition {
    const faceDown = this.getFaceDownPositions(grid);
    if (faceDown.length === 0) {
      return { row: 0, col: 0 };
    }

    // Score each face-down position by strategic value
    let bestPos = faceDown[0];
    let bestScore = -Infinity;

    for (const pos of faceDown) {
      let score = 0;
      const otherRow = pos.row === 0 ? 1 : 0;
      const otherCell = grid[otherRow][pos.col];

      if (otherCell.faceUp) {
        // Revealing in a column where we know the other card gives us
        // information about whether a pair is possible
        score += 3;

        // Higher value in the other cell means more potential savings from a pair
        score += getCardValue(otherCell.card) * 0.5;
      } else {
        // Both cards in column are face-down — less strategic value
        score += 1;
      }

      if (score > bestScore) {
        bestScore = score;
        bestPos = pos;
      }
    }

    return bestPos;
  }


  // ==========================================================================
  // Hard Difficulty — Card Counting & Probability Estimation
  // ==========================================================================

  /**
   * Estimate the expected value of cards remaining in the draw pile
   * by tracking all visible cards.
   */
  private estimateDrawPileExpectedValue(gameState: GolfGameState): number {
    const visibleCards = this.getAllVisibleCards(gameState);
    const totalDeckSize = gameState.playerCount > 2 ? 104 : 52;

    // Calculate total value of all cards in a full deck
    const fullDeckTotalValue = this.calculateFullDeckTotalValue(gameState.playerCount > 2);

    // Subtract visible card values
    let visibleTotal = 0;
    for (const card of visibleCards) {
      visibleTotal += getCardValue(card);
    }

    const remainingCards = totalDeckSize - visibleCards.length;
    if (remainingCards <= 0) {
      return 5; // Fallback average
    }

    const remainingTotal = fullDeckTotalValue - visibleTotal;
    return remainingTotal / remainingCards;
  }

  /**
   * Get all cards visible to the AI (face-up cards in all grids, discard pile, etc.)
   */
  private getAllVisibleCards(gameState: GolfGameState): Card[] {
    const visible: Card[] = [];

    // All face-up cards in all player grids
    for (const player of gameState.players) {
      for (const row of player.grid) {
        for (const cell of row) {
          if (cell.faceUp) {
            visible.push(cell.card);
          }
        }
      }
    }

    // All cards in the discard pile are visible
    for (const card of gameState.discardPile) {
      visible.push(card);
    }

    return visible;
  }

  /**
   * Calculate the total point value of a full deck (or double deck).
   */
  private calculateFullDeckTotalValue(doubleDeck: boolean): number {
    // Single deck: 4 suits × (A=1 + 2+3+4+5+6+7+8+9+10 + J=10 + Q=10 + K=0)
    // = 4 × (1+2+3+4+5+6+7+8+9+10+10+10+0) = 4 × 75 = 300
    const singleDeckValue = 4 * (1 + 2 + 3 + 4 + 5 + 6 + 7 + 8 + 9 + 10 + 10 + 10 + 0);
    return doubleDeck ? singleDeckValue * 2 : singleDeckValue;
  }

  /**
   * Estimate the expected benefit of drawing from the draw pile.
   */
  private estimateExpectedDrawBenefit(grid: CardGrid, expectedDrawValue: number): number {
    // Find the highest value we could replace
    const highest = this.findHighestFaceUpPosition(grid);
    const faceDown = this.getFaceDownPositions(grid);

    let benefit = 0;

    if (highest && expectedDrawValue < highest.value) {
      benefit = highest.value - expectedDrawValue;
    }

    // Also consider replacing face-down cards (expected value ~5.5)
    if (faceDown.length > 0 && expectedDrawValue < 4) {
      const faceDownBenefit = 5.5 - expectedDrawValue;
      benefit = Math.max(benefit, faceDownBenefit * 0.6); // Discount for uncertainty
    }

    return benefit;
  }

  /**
   * Calculate the benefit of replacing a card with the given card.
   */
  private calculateReplacementBenefit(card: Card, grid: CardGrid): number {
    const cardValue = getCardValue(card);
    let maxBenefit = 0;

    // Check column pair creation (highest benefit)
    for (let col = 0; col < 3; col++) {
      for (let row = 0; row < 2; row++) {
        const otherRow = row === 0 ? 1 : 0;
        const otherCell = grid[otherRow][col];
        if (otherCell.faceUp && otherCell.card.rank === card.rank) {
          // Creating a pair: saves both card values
          const currentCell = grid[row][col];
          const currentCost = currentCell.faceUp ? getCardValue(currentCell.card) : 5.5;
          const pairSavings = cardValue + getCardValue(otherCell.card);
          const benefit = currentCost + pairSavings - cardValue;
          maxBenefit = Math.max(maxBenefit, benefit);
        }
      }
    }

    // Check simple replacement benefit
    const highest = this.findHighestFaceUpPosition(grid);
    if (highest && cardValue < highest.value) {
      const benefit = highest.value - cardValue;
      maxBenefit = Math.max(maxBenefit, benefit);
    }

    // Check replacing face-down cards
    const faceDown = this.getFaceDownPositions(grid);
    if (faceDown.length > 0 && cardValue <= 2) {
      const benefit = 5.5 - cardValue; // Expected face-down value minus drawn value
      maxBenefit = Math.max(maxBenefit, benefit * 0.7);
    }

    return maxBenefit;
  }

  /**
   * Find the optimal position to replace considering all factors.
   */
  private findOptimalReplacement(drawnCard: Card, grid: CardGrid, gameState: GolfGameState): GridPosition | null {
    const drawnValue = getCardValue(drawnCard);
    let bestPosition: GridPosition | null = null;
    let bestBenefit = 0;

    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const cell = grid[row][col];
        let benefit = 0;

        if (cell.faceUp) {
          const cellValue = getCardValue(cell.card);
          // Direct replacement benefit
          benefit = cellValue - drawnValue;

          // Check if this column already has a pair (don't break it)
          const otherRow = row === 0 ? 1 : 0;
          const otherCell = grid[otherRow][col];
          if (otherCell.faceUp && otherCell.card.rank === cell.card.rank) {
            // This column is already a pair — don't replace unless drawn creates pair too
            if (drawnCard.rank !== cell.card.rank) {
              benefit = -100; // Heavily penalize breaking a pair
            }
          }
        } else {
          // Face-down card: expected value is ~5.5
          // Only replace if drawn value is significantly lower
          if (drawnValue <= 2) {
            benefit = 5.5 - drawnValue;
            // Discount for uncertainty
            benefit *= 0.6;
          } else if (drawnCard.rank === 'K') {
            benefit = 5.5; // King is 0, expected savings
            benefit *= 0.6;
          } else {
            benefit = 0;
          }
        }

        // Bonus for column-pair potential
        const otherRow = row === 0 ? 1 : 0;
        const otherCell = grid[otherRow][col];
        if (otherCell.faceUp && otherCell.card.rank === drawnCard.rank) {
          // This replacement creates a column pair!
          benefit += getCardValue(otherCell.card) + drawnValue;
        }

        if (benefit > bestBenefit) {
          bestBenefit = benefit;
          bestPosition = { row, col };
        }
      }
    }

    // Only replace if there's meaningful benefit (threshold > 1 point)
    if (bestBenefit > 1) {
      return bestPosition;
    }

    // For Kings, always find somewhere to put them
    if (drawnCard.rank === 'K') {
      const faceDown = this.getFaceDownPositions(grid);
      if (faceDown.length > 0) {
        return faceDown[0];
      }
      const highest = this.findHighestFaceUpPosition(grid);
      if (highest && highest.value > 0) {
        return highest.position;
      }
    }

    return bestPosition;
  }


  // ==========================================================================
  // Shared Helper Methods
  // ==========================================================================

  /**
   * Check if a card can create a column pair in the grid.
   * Returns true if there's a face-up card with matching rank in any column
   * where the other position is available for replacement.
   */
  private canCreateColumnPair(card: Card, grid: CardGrid): boolean {
    return this.findColumnPairPosition(card, grid) !== null;
  }

  /**
   * Find a position where placing the card would create a column pair.
   * Returns the position to place the card, or null if no pair is possible.
   */
  private findColumnPairPosition(card: Card, grid: CardGrid): GridPosition | null {
    for (let col = 0; col < 3; col++) {
      for (let row = 0; row < 2; row++) {
        const otherRow = row === 0 ? 1 : 0;
        const otherCell = grid[otherRow][col];
        const thisCell = grid[row][col];

        // The other cell must be face-up with matching rank
        if (otherCell.faceUp && otherCell.card.rank === card.rank) {
          // This cell should not already be part of a matching pair
          if (!thisCell.faceUp || thisCell.card.rank !== card.rank) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  /**
   * Get the highest card value among face-up cards in the grid.
   */
  private getHighestFaceUpValue(grid: CardGrid): number {
    let highest = 0;
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const cell = grid[row][col];
        if (cell.faceUp) {
          const value = getCardValue(cell.card);
          if (value > highest) {
            highest = value;
          }
        }
      }
    }
    return highest;
  }

  /**
   * Find the position and value of the highest face-up card.
   */
  private findHighestFaceUpPosition(grid: CardGrid): { position: GridPosition; value: number } | null {
    let best: { position: GridPosition; value: number } | null = null;

    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const cell = grid[row][col];
        if (cell.faceUp) {
          const value = getCardValue(cell.card);
          // Don't consider cards that are part of a column pair
          const otherRow = row === 0 ? 1 : 0;
          const otherCell = grid[otherRow][col];
          if (otherCell.faceUp && otherCell.card.rank === cell.card.rank) {
            continue; // Skip paired cards
          }
          if (!best || value > best.value) {
            best = { position: { row, col }, value };
          }
        }
      }
    }

    return best;
  }

  /**
   * Get all face-down positions in the grid.
   */
  private getFaceDownPositions(grid: CardGrid): GridPosition[] {
    const positions: GridPosition[] = [];
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        if (!grid[row][col].faceUp) {
          positions.push({ row, col });
        }
      }
    }
    return positions;
  }

  /**
   * Find the best face-down position to replace, considering column-pair potential.
   */
  private findBestFaceDownForReplacement(card: Card, grid: CardGrid, faceDown: GridPosition[]): GridPosition {
    // Prefer a position where the other card in the column matches rank
    for (const pos of faceDown) {
      const otherRow = pos.row === 0 ? 1 : 0;
      const otherCell = grid[otherRow][pos.col];
      if (otherCell.faceUp && otherCell.card.rank === card.rank) {
        return pos; // Creates a column pair
      }
    }

    // Otherwise prefer columns where the other card is face-down (more flexibility)
    const unknownColumns = faceDown.filter(pos => {
      const otherRow = pos.row === 0 ? 1 : 0;
      return !grid[otherRow][pos.col].faceUp;
    });

    if (unknownColumns.length > 0) {
      return unknownColumns[Math.floor(Math.random() * unknownColumns.length)];
    }

    return faceDown[Math.floor(Math.random() * faceDown.length)];
  }

  /**
   * Shuffle an array using Fisher-Yates (for internal randomization).
   */
  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Estimate the current AI player's score based on visible cards.
   * Face-down cards are estimated at average value (~5.5).
   */
  private estimateOwnScore(grid: CardGrid): number {
    let score = 0;
    const cancelledCols = new Set<number>();

    // Check for column pairs
    for (let col = 0; col < 3; col++) {
      const top = grid[0][col];
      const bottom = grid[1][col];
      if (top.faceUp && bottom.faceUp && top.card.rank === bottom.card.rank) {
        cancelledCols.add(col);
      }
    }

    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        if (cancelledCols.has(col)) continue;
        const cell = grid[row][col];
        if (cell.faceUp) {
          score += getCardValue(cell.card);
        } else {
          score += 5.5; // Expected average value for unknown card
        }
      }
    }

    return score;
  }

  /**
   * Determine if the AI should knock based on difficulty-specific logic.
   * This is called externally by the game engine to check knock intent.
   * Note: Auto-knock happens when all cards are face-up regardless.
   * This method is for strategic knock decisions (not currently triggered
   * since Golf auto-knocks when all cards are revealed).
   */
  shouldKnock(gameState: GolfGameState): boolean {
    const aiPlayerIndex = gameState.currentPlayerIndex;
    const grid = gameState.players[aiPlayerIndex].grid;
    const faceUpCount = this.countFaceUp(grid);

    switch (this.difficulty) {
      case 'easy':
        // Knock at random times when most cards revealed
        return faceUpCount >= 5 && Math.random() < 0.3;

      case 'medium': {
        // Knock when estimated score is below average
        if (faceUpCount < 5) return false;
        const estimatedScore = this.estimateOwnScore(grid);
        // Average round score is roughly 15-20 for a decent hand
        return estimatedScore < 12;
      }

      case 'hard': {
        // Optimal knock timing based on opponent estimates and round
        if (faceUpCount < 5) return false;
        const ownScore = this.estimateOwnScore(grid);

        // Estimate opponent scores
        let avgOpponentEstimate = 0;
        let opponentCount = 0;
        for (let i = 0; i < gameState.players.length; i++) {
          if (i === aiPlayerIndex) continue;
          avgOpponentEstimate += this.estimateOwnScore(gameState.players[i].grid);
          opponentCount++;
        }
        if (opponentCount > 0) {
          avgOpponentEstimate /= opponentCount;
        }

        // Consider round number: later rounds may warrant more aggressive knocking
        const roundFactor = gameState.currentRound / gameState.totalRounds;

        // Knock if our score is significantly below opponents
        // and we're confident (most cards revealed)
        const threshold = faceUpCount === 6 ? 0 : 3;
        return ownScore < avgOpponentEstimate - threshold - (roundFactor * 2);
      }
    }
  }

  /**
   * Count face-up cards in a grid.
   */
  private countFaceUp(grid: CardGrid): number {
    let count = 0;
    for (const row of grid) {
      for (const cell of row) {
        if (cell.faceUp) count++;
      }
    }
    return count;
  }
}
